import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Settings.css';
import api from '../utils/api';

const Settings = ({ user, onUserUpdate }) => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    payout_doge_address: user.payout_doge_address || '',
    username: user.username || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [userStats, setUserStats] = useState({
    credits: user.credits || 0,
    wins: user.wins || 0,
    created_at: user.created_at
  });
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [payoutTotals, setPayoutTotals] = useState({ total_payouts: 0, total_amount: 0 });
  const [loadingPayouts, setLoadingPayouts] = useState(false);

  const loadUserStats = async () => {
    try {
      const response = await api.get('/api/auth/me');
      if (response.data.user) {
        setUserStats({
          credits: response.data.user.credits || 0,
          wins: response.data.user.wins || 0,
          created_at: response.data.user.created_at
        });
        setFormData({
          payout_doge_address: response.data.user.payout_doge_address || '',
          username: response.data.user.username || ''
        });
      }
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const loadPayoutHistory = useCallback(async () => {
    try {
      setLoadingPayouts(true);
      const response = await api.get(`/api/users/${user.id}/payouts`);
      setPayoutHistory(response.data.payouts || []);
      setPayoutTotals(response.data.totals || { total_payouts: 0, total_amount: 0 });
    } catch (error) {
      console.error('Error loading payout history:', error);
    } finally {
      setLoadingPayouts(false);
    }
  }, [user.id]);

  useEffect(() => {
    loadUserStats();
    loadPayoutHistory();
  }, [loadPayoutHistory]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/api/auth/settings', {
        payout_doge_address: formData.payout_doge_address
      });

      setMessage({ type: 'success', text: 'Settings updated successfully!' });
      
      // Update parent component
      if (onUserUpdate) {
        onUserUpdate({
          ...user,
          payout_doge_address: formData.payout_doge_address
        });
      }

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="settings-container">
      <div className="settings-window win98-bevel">
        <div className="navigation-bar win98-bevel-inset">
          <div className="nav-links">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
              💬 Chat
            </Link>
            <Link to="/puddle" className={`nav-link ${location.pathname === '/puddle' ? 'active' : ''}`}>
              💎 Puddle
            </Link>
            <Link to="/leaderboard" className={`nav-link ${location.pathname === '/leaderboard' ? 'active' : ''}`}>
              🏆 Leaderboard
            </Link>
            <Link to="/settings" className={`nav-link ${location.pathname === '/settings' ? 'active' : ''}`}>
              ⚙️ Settings
            </Link>
            <Link to="/faq" className={`nav-link ${location.pathname === '/faq' ? 'active' : ''}`}>
              ❓ FAQ
            </Link>
            <Link to="/tos" className={`nav-link ${location.pathname === '/tos' ? 'active' : ''}`}>
              📜 Rules/ToS
            </Link>
            <a 
              href="https://discord.gg/B26JbHZsqq" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="nav-link discord-link"
              title="Join our Discord"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0824 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0824 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 49.9718 48.5383C51.038 50.6034 52.2554 52.5699 53.5951 54.435C53.651 54.5139 53.7489 54.5477 53.8413 54.5195C59.646 52.7249 65.5286 50.0174 71.6015 45.5576C71.6551 45.5182 71.6887 45.459 71.6943 45.3942C73.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.2991 34.8216 17.2991 31.6709C17.2991 28.5203 20.1717 26.0166 23.7259 26.0166C27.308 26.0166 30.1626 28.4935 30.1526 31.6709C30.1426 34.8483 27.308 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.8911 34.8216 40.8911 31.6709C40.8911 28.5203 43.7636 26.0166 47.3178 26.0166C50.9 26.0166 53.7545 28.4935 53.7446 31.6709C53.7346 34.8483 50.9 37.3253 47.3178 37.3253Z" fill="#5865F2"/>
              </svg>
              Discord
            </a>
          </div>
        </div>
        <div className="settings-header">
          <h1 className="settings-title">⚙️ Settings ⚙️</h1>
        </div>

        <div className="settings-content">
          <div className="settings-section win98-bevel-inset">
            <h2 className="section-title">Account Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Username:</span>
                <span className="info-value">{formData.username}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Credits:</span>
                <span className="info-value">💰 {userStats.credits.toFixed(2)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Daily Wins:</span>
                <span className="info-value">🏆 {userStats.wins}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Member Since:</span>
                <span className="info-value">{formatDate(userStats.created_at)}</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="settings-form">
            <div className="settings-section win98-bevel-inset">
              <h2 className="section-title">Payout Settings</h2>
              <div className="form-group">
                <label htmlFor="payout_doge_address" className="form-label">
                  DOGE Wallet Address:
                </label>
                <input
                  type="text"
                  id="payout_doge_address"
                  name="payout_doge_address"
                  value={formData.payout_doge_address}
                  onChange={handleChange}
                  className="form-input win98-bevel-inset"
                  placeholder="D... (Your DOGE address for receiving winnings)"
                  required
                />
                <p className="form-help">
                  This address will receive your daily contest winnings. Make sure it's correct!
                </p>
                <div className="form-warning win98-bevel-inset" style={{ marginTop: '10px', padding: '10px', background: '#fff3cd', border: '2px inset #808080' }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>⚠️ Important Information:</p>
                  <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '0.9rem' }}>
                    <li><strong>Address Change Lock:</strong> Address changes are locked 1 hour before and after payout time (11:00 PM - 12:59 AM UTC) to prevent manipulation</li>
                    <li><strong>Minimum Payouts:</strong> Small prizes are automatically accumulated. If you win multiple times, small amounts will be combined and sent once they reach the minimum</li>
                    <li><strong>Double-check your address:</strong> Once set, make sure it's correct - payouts are automatic and cannot be reversed!</li>
                  </ul>
                </div>
              </div>
            </div>

            {message.text && (
              <div className={`message ${message.type}`}>
                {message.text}
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="win98-button save-button"
                disabled={loading}
              >
                {loading ? 'Saving...' : '💾 Save Settings'}
              </button>
            </div>
          </form>

          <div className="settings-section win98-bevel-inset">
            <h2 className="section-title">💰 Payout History</h2>
            {loadingPayouts ? (
              <div className="loading-text">Loading payout history... 🐕</div>
            ) : payoutHistory.length === 0 ? (
              <div className="empty-state">
                <p>No payouts yet. Win a daily contest to see your winnings here! 🏆</p>
              </div>
            ) : (
              <div className="payout-history">
                <div className="payout-totals">
                  <p><strong>Total Payouts:</strong> {payoutTotals.total_payouts}</p>
                  <p><strong>Total Amount Won:</strong> {payoutTotals.total_amount.toFixed(8)} DOGE</p>
                </div>
                <div className="payout-list">
                  {payoutHistory.map((payout) => (
                    <div key={payout.id} className="payout-item win98-bevel-inset">
                      <div className="payout-header">
                        <span className="payout-rank">
                          {payout.rank === 1 ? '🥇' : payout.rank === 2 ? '🥈' : '🥉'} 
                          Rank #{payout.rank}
                        </span>
                        <span className="payout-amount">{payout.amount.toFixed(8)} DOGE</span>
                      </div>
                      <div className="payout-details">
                        <p><strong>Date:</strong> {new Date(payout.payout_date).toLocaleDateString()}</p>
                        {payout.message && (
                          <p className="payout-message">
                            <strong>Winning Message:</strong> "{payout.message.content.substring(0, 100)}{payout.message.content.length > 100 ? '...' : ''}" 
                            ({payout.message.upvotes} upvotes)
                          </p>
                        )}
                        {payout.nowpayments_payout_id && (
                          <p className="payout-id"><strong>Transaction ID:</strong> {payout.nowpayments_payout_id}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="settings-section win98-bevel-inset">
            <h2 className="section-title">🎨 Retro Settings</h2>
            <div className="settings-options">
              <div className="setting-option">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={localStorage.getItem('scanlinesEnabled') === 'true'}
                    onChange={(e) => {
                      localStorage.setItem('scanlinesEnabled', e.target.checked);
                      window.location.reload(); // Reload to apply scanlines
                    }}
                  />
                  <span>Enable Scanline Effect (CRT monitor look)</span>
                </label>
              </div>
              <div className="setting-option">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={localStorage.getItem('soundsEnabled') === 'true'}
                    onChange={(e) => {
                      localStorage.setItem('soundsEnabled', e.target.checked);
                    }}
                  />
                  <span>Enable Sound Effects (Windows 98 click sounds)</span>
                </label>
                <p className="setting-help">Sounds are OFF by default. Enable to hear retro click sounds.</p>
              </div>
              <div className="setting-option">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={localStorage.getItem('retroCursor') === 'true'}
                    onChange={(e) => {
                      localStorage.setItem('retroCursor', e.target.checked);
                      window.location.reload(); // Reload to apply cursor
                    }}
                  />
                  <span>Enable Retro Cursor</span>
                </label>
              </div>
            </div>
          </div>

          <div className="settings-section win98-bevel-inset">
            <h2 className="section-title">About Doge Of The Day</h2>
            <div className="about-content">
              <p>🐕 Doge Of The Day is a pay-to-post chat platform where:</p>
              <ul>
                <li>💰 Each message costs 1 Credit (1 DOGE = 1 Credit)</li>
                <li>🏆 Daily contests reward top 3 messages with DOGE prizes (60%, 25%, 15%)</li>
                <li>⬆️ Most upvoted messages win prizes at midnight UTC!</li>
                <li>💳 Deposit 20 DOGE minimum to get started</li>
                <li>📖 See FAQ page for detailed reward structure</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;

